/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

export function removeInlineZoomStyle(): void {
    const zoomedImages: HTMLCollectionOf<Element> = document.body.getElementsByClassName('ms-inline-zoom__zoomedImg');

    // eslint-disable-next-line xss/no-mixed-html -- Need raw HTML.
    const zoomedImagesArray = Array.from(zoomedImages) as HTMLImageElement[];
    for (const item of zoomedImagesArray) {
        item.style.width = '0';
        item.style.height = '0';
    }
}

/**
 * Represents default data scale value which should be used in case improper value specified in module config.
 */
export const defaultDataScale = 2;

/**
 * Clears zooming state and reverts to initial image.
 * @param event - Event of moving the mouse out of the image.
 */
export function onMouseOutLensContainer(event: React.MouseEvent<HTMLDivElement>): void {
    const zoomLens = event.currentTarget;
    zoomLens.style.visibility = 'hidden';
    zoomLens.classList.remove('ms-containerZoom__zoom-lens__opacity');

    const zoomedImageContainer = document.body.querySelector<HTMLDivElement>('.ms-containerZoom__result');
    if (zoomedImageContainer) {
        zoomedImageContainer.style.visibility = 'hidden';
    }
}

/**
 * Represents size dimensions.
 */
interface ISize {
    width: number;
    height: number;
}

/**
 * Represents two-dimensional position.
 */
interface ILocation {

    /**
     * Left offset.
     */
    x: number; // eslint-disable-line @typescript-eslint/naming-convention -- To align with html standards.

    /**
     * Top offset.
     */
    y: number; // eslint-disable-line @typescript-eslint/naming-convention -- To align with html standards.
}

/**
 * Retrieves the displayed image size preserving image proportions.
 * @param imageElement - The html image element which is displayed.
 * @returns The dimensions of the displayed image ignoring all the offsets and paddings.
 */
function getDisplayedImageSize(imageElement: HTMLImageElement): ISize {
    const imageInitialHeight = imageElement.naturalHeight;
    const imageInitialWidth = imageElement.naturalWidth;
    const imageContainerHeight = imageElement.height;
    const imageContainerWidth = imageElement.width;

    const widthScaleRatio = imageContainerWidth / imageInitialWidth;
    const heightScaleRatio = imageContainerHeight / imageInitialHeight;

    const scaleRatio = Math.min(widthScaleRatio, heightScaleRatio);

    return {
        width: imageInitialWidth * scaleRatio,
        height: imageInitialHeight * scaleRatio
    };
}

/**
 * Retrieves the center point based on the given dimensions.
 * @param size - Dimensions of an element.
 * @returns The point of the center.
 */
function getCenter(size: ISize): ILocation {
    const centerDivider = 2;
    return {
        x: size.width / centerDivider,
        y: size.height / centerDivider
    };
}

/**
 * Retrieves the position where the lens must be placed based by the mouse position, lens size, and container size.
 * @param cursorPosition - The location of the mouse.
 * @param lensSize - The size of the lens.
 * @param originalSize - The container in which the lens should be placed.
 * @returns The location where the lens must be placed.
 */
function getLensPosition(cursorPosition: ILocation, lensSize: ISize, originalSize: ISize) {
    const lensCenter = getCenter(lensSize);

    const position: ILocation = {
        x: cursorPosition.x - lensCenter.x,
        y: cursorPosition.y - lensCenter.y
    };

    // Limit lens to be inside the image element.
    const minPositionX = 0;
    const maxPositionX = originalSize.width - lensSize.width;
    position.x = Math.min(Math.max(position.x, minPositionX), maxPositionX);

    const minPositionY = 0;
    const maxPositionY = originalSize.height - lensSize.height;
    position.y = Math.min(Math.max(position.y, minPositionY), maxPositionY);

    return position;
}

/**
 * Retrieves mouse position.
 * @param event - Mouse event.
 * @param element - Element in which the event happened.
 * @returns Mouse position inside the given element.
 */
function getCursorPosition(event: React.MouseEvent, element: HTMLElement): ILocation {
    const containerBoundingRect = element.getBoundingClientRect();
    return {
        x: event.pageX - containerBoundingRect.left - window.pageXOffset,
        y: event.pageY - containerBoundingRect.top - window.pageYOffset
    };
}

/**
 * Formats number to represent pixels, e.g. 42px.
 * @param numericValue - The numeric value of pixels.
 * @returns String in pixel format.
 */
function formatPixelsValue(numericValue: number): string {
    return `${numericValue}px`;
}

/**
 * Formats the given url to be a background image style value.
 * @param sourceUrl - The image source.
 * @returns Background image style value.
 */
function formatBackgroundImageUrl(sourceUrl: string): string {
    return `url('${sourceUrl}')`;
}

/**
 * Get the cursor position on image click.
 * @param zoomedImage - The target image.
 * @param dataScaleText - Zoom factor for image.
 * @param event - Mouse event action.
 */
function handleInlineStyles(zoomedImage: EventTarget & HTMLImageElement, dataScaleText: string, event: React.MouseEvent<HTMLImageElement>): void {
    zoomedImage.removeAttribute('style');
    const imageElement = zoomedImage.parentElement!.previousElementSibling!.querySelector('img')!;

    const dataScale = Number(dataScaleText);

    const imageContainerSize: ISize = {
        width: imageElement.offsetWidth,
        height: imageElement.offsetHeight
    };

    const zoomedImageSize: ISize = {
        width: imageContainerSize.width * dataScale,
        height: imageContainerSize.height * dataScale
    };

    zoomedImage.style.width = formatPixelsValue(zoomedImageSize.width);
    zoomedImage.style.height = formatPixelsValue(zoomedImageSize.height);
    zoomedImage.style.opacity = '1';

    const cursorPosition = getCursorPosition(event, imageElement);
    const imageCenter = getCenter(imageContainerSize);
    const offset: ILocation = {
        x: cursorPosition.x - imageCenter.x,
        y: cursorPosition.y - imageCenter.y
    };

    const scaledCursorPosition: ILocation = {
        x: cursorPosition.x * dataScale,
        y: cursorPosition.y * dataScale
    };
    scaledCursorPosition.x -= offset.x;
    scaledCursorPosition.y -= offset.y;

    const zoomedImagePosition = getLensPosition(scaledCursorPosition, imageContainerSize, zoomedImageSize);

    zoomedImage.style.left = formatPixelsValue(-zoomedImagePosition.x);
    zoomedImage.style.top = formatPixelsValue(-zoomedImagePosition.y);
}

/**
 * ZoomIn functionality on image click.
 * @param event -The mouse event.
 * @param dataScale -The zoom factor.
 */
export function inlineZoomInitClick(event: React.MouseEvent<HTMLImageElement>, dataScale?: string): void {
    if (event.type === 'touchmove' || event.type === 'touchstart' || event.type === 'touchend') {
        return;
    }

    const zoomImg = event.currentTarget.parentElement?.nextElementSibling?.querySelector('img');
    if (!zoomImg) {
        return;
    }

    handleInlineStyles(zoomImg, dataScale ?? '2', event);
}

/**
 * Updates lens and zoomed image positions on the page.
 * @param event - Mouse movement event.
 * @param imageElement - Original image element.
 * @param lens - The element which shows the part of the image which is zoomed.
 */
function handleMouseMovementInContainerZoom(
    event: React.MouseEvent<HTMLDivElement>, imageElement: HTMLImageElement, lens: EventTarget & HTMLDivElement): void {

    let zoomedContainer = document.body.querySelector<HTMLDivElement>('.ms-containerZoom__result');
    if (!zoomedContainer) {
        zoomedContainer = document.createElement('div');
        zoomedContainer.setAttribute('class', 'ms-containerZoom__result');
        document.body.appendChild(zoomedContainer);
    }

    const lensSize: ISize = {
        width: lens.offsetWidth,
        height: lens.offsetHeight
    };

    const dataScale = Number(lens.getAttribute('data-scale') ?? defaultDataScale);

    const cursorPosition = getCursorPosition(event, imageElement);
    const displayedImageSize = getDisplayedImageSize(imageElement);
    const imageContainerSize: ISize = {
        width: imageElement.offsetWidth,
        height: imageElement.offsetHeight
    };

    const lensPosition = getLensPosition(cursorPosition, lensSize, imageContainerSize);
    lens.style.visibility = 'unset';
    lens.style.left = formatPixelsValue(lensPosition.x);
    lens.style.top = formatPixelsValue(lensPosition.y);

    const mainImageSource = imageElement.getAttribute('src')!;

    zoomedContainer.style.visibility = 'unset';
    zoomedContainer.style.backgroundImage = formatBackgroundImageUrl(mainImageSource);
    zoomedContainer.style.width = formatPixelsValue(lensSize.width * dataScale);
    zoomedContainer.style.height = formatPixelsValue(lensSize.width * dataScale);

    const offsetSize: ISize = {
        width: imageContainerSize.width - displayedImageSize.width,
        height: imageContainerSize.height - displayedImageSize.height
    };
    const offsetCenter = getCenter(offsetSize);
    const lensScaledPositionX = (offsetCenter.x - lensPosition.x) * dataScale;
    const lensScaledPositionY = (offsetCenter.y - lensPosition.y) * dataScale;

    zoomedContainer.style.backgroundPositionX = formatPixelsValue(lensScaledPositionX);
    zoomedContainer.style.backgroundPositionY = formatPixelsValue(lensScaledPositionY);

    zoomedContainer.style.backgroundSize =
        `${formatPixelsValue(displayedImageSize.width * dataScale)} ${formatPixelsValue(displayedImageSize.height * dataScale)}`;

    const topPosition = imageElement.getBoundingClientRect().top;
    zoomedContainer.style.top = formatPixelsValue(topPosition);
}

export function onMouseOverImageContainer(event: React.MouseEvent<HTMLDivElement>): void {
    const lens = event.currentTarget.parentElement!.previousElementSibling! as HTMLDivElement;
    lens.classList.add('ms-containerZoom__zoom-lens__opacity');

    const imageElement = event.currentTarget as HTMLImageElement;

    handleMouseMovementInContainerZoom(event, imageElement, lens);
}

export function onMouseMoveLensContainer(event: React.MouseEvent<HTMLDivElement>): void {
    if (event.type === 'touchmove' || event.type === 'touchstart' || event.type === 'touchend') {
        return;
    }

    const imageElement = event.currentTarget.nextElementSibling!.querySelector('img')!;
    const lens = event.currentTarget;

    handleMouseMovementInContainerZoom(event, imageElement, lens);
}

export function onContainerZoomInit(event: React.MouseEvent<HTMLImageElement>): void {
    if (!document.body.querySelector('.ms-containerZoom__result')) {
        const containerDiv = document.createElement('div');
        containerDiv.setAttribute('class', 'ms-containerZoom__result');
        document.body.appendChild(containerDiv);
    }

    onMouseOverImageContainer(event);
}

export function inlineZoomImageOnMouseMove(event: React.MouseEvent<HTMLImageElement>): void {
    if (event.type === 'touchmove' || event.type === 'touchstart' || event.type === 'touchend') {
        return;
    }

    const parentElement = event.currentTarget.parentElement!.parentElement!;
    const zoomImg = event.currentTarget;

    const dataScale = parentElement.getAttribute('data-scale') ?? `${defaultDataScale}`;
    handleInlineStyles(zoomImg, dataScale, event);
}

export function inlineZoomImageOnMouseOut(event: React.MouseEvent<HTMLImageElement>): void {
    if (event.type === 'touchmove' || event.type === 'touchstart' || event.type === 'touchend') {
        return;
    }

    event.currentTarget.style.height = '0';
    event.currentTarget.style.width = '0';
}

export function inlineZoomInit(event: React.MouseEvent<HTMLImageElement>, dataScale: string = '2'): void {
    if (event.type === 'touchmove' || event.type === 'touchstart' || event.type === 'touchend') {
        return;
    }

    const zoomedImage = event.currentTarget.parentElement?.nextElementSibling?.querySelector('img');
    if (!zoomedImage) {
        return;
    }

    handleInlineStyles(zoomedImage, dataScale, event);
}

export function inlineZoomImageOnHover(event: React.MouseEvent<HTMLImageElement>, scale: string = '2'): void {
    if (event.type === 'touchmove' || event.type === 'touchstart' || event.type === 'touchend') {
        return;
    }

    const zoomedImg = event.currentTarget.parentElement!.nextElementSibling!.querySelector('img')!;
    handleInlineStyles(zoomedImg, scale, event);
}

/**
 * Remove container zoomed image.
 */
export function removeContainerZoomStyle(): void {

    const zoomLens = document.body.querySelector<HTMLDivElement>('.ms-containerZoom__zoom-lens');
    if (zoomLens) {
        zoomLens.style.visibility = 'hidden';
        zoomLens.classList.remove('ms-containerZoom__zoom-lens__opacity');
    }

    const zoomedImageContainer = document.body.querySelector<HTMLDivElement>('.ms-containerZoom__result');
    if (zoomedImageContainer) {
        zoomedImageContainer.style.visibility = 'hidden';
    }
}
